import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import "./ApplicationLogs.css";

import {
    getApplicationLogsRequest,
} from "../../../redux/actions/applicationLogActions";

const STATUS_CONFIG = {
    SUCCESS: {
        label: "Succès",
        color: "#166534",
        bg: "#dcfce7",
    },
    FAILED: {
        label: "Échec",
        color: "#b91c1c",
        bg: "#fee2e2",
    },
};

const formatDateTime = (value) => {
    if (!value) return "—";
    try {
        const date = new Date(value);
        return date.toLocaleString("fr-FR");
    } catch {
        return value;
    }
};

const ApplicationLogs = ({ logs, loading, error, getApplicationLogs }) => {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [moduleFilter, setModuleFilter] = useState("ALL");

    useEffect(() => {
        getApplicationLogs();
    }, []);

    const modules = useMemo(() => {
        const list = (logs || [])
            .map((log) => log.module)
            .filter(Boolean);

        return [...new Set(list)].sort();
    }, [logs]);

    const filteredLogs = useMemo(() => {
        const term = search.trim().toLowerCase();

        return (logs || []).filter((log) => {
            const matchesSearch =
                !term ||
                (log.module || "").toLowerCase().includes(term) ||
                (log.action || "").toLowerCase().includes(term) ||
                (log.entityName || "").toLowerCase().includes(term) ||
                String(log.entityId || "").toLowerCase().includes(term) ||
                (log.description || "").toLowerCase().includes(term) ||
                (log.username || "").toLowerCase().includes(term) ||
                (log.fullName || "").toLowerCase().includes(term) ||
                (log.errorMessage || "").toLowerCase().includes(term);

            const matchesStatus =
                statusFilter === "ALL" || log.status === statusFilter;

            const matchesModule =
                moduleFilter === "ALL" || log.module === moduleFilter;

            return matchesSearch && matchesStatus && matchesModule;
        });
    }, [logs, search, statusFilter, moduleFilter]);

    return (
        <div className="application-logs-page">
            <div className="application-logs-header">
                <div>
                    <h3>Logs applicatifs</h3>
                    <p>Consultez les actions effectuées dans l’application.</p>
                </div>

                <button className="application-logs-refresh-btn" onClick={getApplicationLogs} disabled={loading}>
                    {loading ? "Chargement..." : "Actualiser"}
                </button>
            </div>

            <div className="application-logs-toolbar">
                <input
                    type="text"
                    className="application-logs-search"
                    placeholder="Rechercher par module, action, utilisateur, description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <select
                    className="application-logs-filter"
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                >
                    <option value="ALL">Tous les modules</option>
                    {modules.map((module) => (
                        <option key={module} value={module}>
                            {module}
                        </option>
                    ))}
                </select>

                <select
                    className="application-logs-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="ALL">Tous les statuts</option>
                    <option value="SUCCESS">Succès</option>
                    <option value="FAILED">Échec</option>
                </select>

                <div className="application-logs-count">
                    {filteredLogs.length} résultat{filteredLogs.length > 1 ? "s" : ""}
                </div>
            </div>

            {error && <div className="application-logs-alert error">{error}</div>}

            <div className="application-logs-table-wrapper">
                <table className="application-logs-table">
                    <thead>
                    <tr>
                        <th>Date</th>
                        <th>Module</th>
                        <th>Action</th>
                        <th>Entité</th>
                        <th>ID</th>
                        <th>Statut</th>
                        <th>Utilisateur</th>
                        <th>Description</th>
                        <th>User Agent</th>
                        <th>Erreur</th>
                    </tr>
                    </thead>

                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={10} className="application-logs-empty">
                                Chargement des logs...
                            </td>
                        </tr>
                    ) : filteredLogs.length === 0 ? (
                        <tr>
                            <td colSpan={10} className="application-logs-empty">
                                Aucun log applicatif trouvé
                            </td>
                        </tr>
                    ) : (
                        filteredLogs.map((log) => {
                            const statusCfg = STATUS_CONFIG[log.status] || {
                                label: log.status || "—",
                                color: "#334155",
                                bg: "#e2e8f0",
                            };

                            return (
                                <tr key={log.id}>
                                    <td>{formatDateTime(log.createdAt)}</td>
                                    <td>{log.module || "—"}</td>
                                    <td>{log.action || "—"}</td>
                                    <td>{log.entityName || "—"}</td>
                                    <td>{log.entityId || "—"}</td>
                                    <td>
                                            <span
                                                className="application-logs-status"
                                                style={{
                                                    color: statusCfg.color,
                                                    background: statusCfg.bg,
                                                }}
                                            >
                                                {statusCfg.label}
                                            </span>
                                    </td>
                                    <td>{log.fullName || log.username || "—"}</td>
                                    <td className="application-logs-description">{log.description || "—"}</td>
                                    <td className="application-logs-useragent">{log.userAgent || "—"}</td>
                                    <td className="application-logs-error-cell">
                                        {log.errorMessage ? (
                                            <span className="application-logs-error-text">{log.errorMessage}</span>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    logs: state.applicationLog?.logs || [],
    loading: state.applicationLog?.loading || false,
    error: state.applicationLog?.error || null,
});

const mapDispatchToProps = (dispatch) => ({
    getApplicationLogs: () => dispatch(getApplicationLogsRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationLogs);